// src/calendario/CrearPlantillaTurno.jsx
import React, { useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";

// Icons
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

export const CrearPlantillaTurno = () => {
  const toast = useToast();

  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [color, setColor] = useState("#85C1E9");
  const [segmentos, setSegmentos] = useState([{ hora_inicio: "", hora_fin: "" }]);

  const [loading, setLoading] = useState(false);

  // =========================
  // Segmentos
  // =========================
  const agregarSegmento = () => {
    setSegmentos((prev) => [...prev, { hora_inicio: "", hora_fin: "" }]);
  };

  const eliminarSegmento = (index) => {
    setSegmentos((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSegmentoChange = (index, campo, valor) => {
    setSegmentos((prev) => prev.map((s, i) => (i === index ? { ...s, [campo]: valor } : s)));
  };

  // =========================
  // Validación
  // =========================
  const validar = () => {
    if (!nombre.trim()) return "El nombre es obligatorio.";
    if (!segmentos.length) return "Añade al menos un segmento.";

    for (let i = 0; i < segmentos.length; i++) {
      const s = segmentos[i];
      if (!s.hora_inicio || !s.hora_fin) return `Completa las horas del segmento ${i + 1}.`;
      // ✅ NO validamos hora_inicio < hora_fin porque el turno puede cruzar medianoche (24/7)
    }

    return "";
  };

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const err = validar();
    if (err) {
      toast.warning(err);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${Global.url}turnos/plantillas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          nombre: nombre.trim(),
          descripcion: (descripcion || "").trim(),
          color,
          segmentos,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Error al crear la plantilla.");
        return;
      }

      toast.success("Plantilla creada correctamente.");
      setNombre("");
      setDescripcion("");
      setColor("#85C1E9");
      setSegmentos([{ hora_inicio: "", hora_fin: "" }]);
    } catch (err2) {
      console.error("Error creando plantilla:", err2);
      toast.error("Error de conexión al crear la plantilla.");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Crear plantilla de turno
        </Typography>

        <Box className="contentContainer contentContainer--formSingle">
          <Paper className="formPaper">
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Nombre del turno"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />

                <TextField
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Opcional"
                />

                {/* Color */}
                <TextField
                  label="Color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          component="input"
                          type="color"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          aria-label="Seleccionar color"
                          style={{
                            width: 44,
                            height: 28,
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Segmentos */}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontWeight: 700 }}>Segmentos (horarios)</Typography>
                    <Button type="button" size="small" startIcon={<AddIcon />} onClick={agregarSegmento}>
                      Añadir segmento
                    </Button>
                  </Stack>

                  <Stack spacing={1}>
                    {segmentos.map((seg, i) => (
                      <Stack
                        key={i}
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{ alignItems: { sm: "end" } }}
                      >
                        <TextField
                          label={`Inicio (segmento ${i + 1})`}
                          type="time"
                          value={seg.hora_inicio}
                          onChange={(e) => handleSegmentoChange(i, "hora_inicio", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          required
                        />

                        <TextField
                          label="Fin"
                          type="time"
                          value={seg.hora_fin}
                          onChange={(e) => handleSegmentoChange(i, "hora_fin", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          required
                        />

                        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                          <IconButton
                            aria-label={`Eliminar segmento ${i + 1}`}
                            onClick={() => eliminarSegmento(i)}
                            disabled={segmentos.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>

                {/* Acciones */}
                <Box className="formActions">
                  <Button type="submit" disabled={loading} startIcon={<SaveIcon />}>
                    {loading ? "Guardando..." : "Crear plantilla"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
